import { useEffect, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import type { StaffOption } from 'app/components/task-templates/TaskTemplateEditorDialog'
import type { TaskTemplate } from 'app/generated/graphql'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { TaskTemplateInput } from 'app/stores/TaskTemplateStore'
import { useTaskTemplateStore } from 'app/stores/StoreContext'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError, logWarn } from 'lib/logging'

export type UseTaskTemplatesReturn = {
  templates: TaskTemplate[]
  saving: boolean
  staffOptions: StaffOption[]
  staffLoading: boolean
  setSearch: (value: string) => void
  editing: TaskTemplate | `new` | undefined
  openNew: () => void
  openEdit: (template: TaskTemplate) => void
  closeEditor: () => void
  saveTemplate: (input: TaskTemplateInput) => Promise<void>
  removeTemplate: (template: TaskTemplate) => Promise<void>
}

export function useTaskTemplates(): UseTaskTemplatesReturn {
  const store = useTaskTemplateStore()
  const workspace = useWorkspace()
  const [confirm] = useConfirmation()
  const [search, setSearch] = useState(``)
  const [editing, setEditing] = useState<TaskTemplate | `new` | undefined>()
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([])
  // True until the staff fetch lands, the owner column shows a quiet dash
  // instead of a false "Former staff" flash while the list is empty.
  const [staffLoading, setStaffLoading] = useState(true)

  useEffect(() => {
    bg(store.load())
  }, [workspace?.id])

  // Workspace staff for the default-owner select + owner-name display.
  useEffect(() => {
    if (!workspace?.id) return
    // Search string hoisted out of the call: the no-inline-graphql-query
    // ast-grep rule flags any template literal inside gql helper arguments.
    const staffSearch = `workspaceId:${workspace.id}`
    bg(
      gqlFetchList<{ id: string; name?: string }>(
        'users',
        { search: staffSearch, pagination: { page: 0, limit: 100 } },
        'id name',
      )
        .then(({ hits }) => {
          setStaffOptions((hits ?? []).filter((u) => u.id).map((u) => ({ id: u.id, name: u.name ?? u.id })))
          setStaffLoading(false)
        })
        // Clear the loading flag on failure too, or the Owner column shows
        // dashes forever rather than falling back to raw ids.
        .catch((err) => {
          logWarn(`useTaskTemplates: staff lookup failed, owner names unavailable`, err)
          setStaffLoading(false)
        }),
    )
  }, [workspace?.id])

  const q = search.trim().toLowerCase()
  const templates = store
    .forWorkspace(workspace?.id)
    .filter((t) => !q || t.name.toLowerCase().includes(q) || t.title.toLowerCase().includes(q))

  const saveTemplate = async (input: TaskTemplateInput) => {
    if (!editing) return
    const result = editing === `new` ? await store.create(input) : await store.update(editing.id, input)
    if (result.error) {
      notifyError(`Couldn't save the task template`)
      logError(`useTaskTemplates: save failed`, result.error.message)
      return
    }
    notifySuccess(editing === `new` ? `Task template created` : `Task template updated`)
    setEditing(undefined)
  }

  const removeTemplate = async (template: TaskTemplate) => {
    const confirmed = await confirm({
      title: `Delete task template`,
      description: `Delete "${template.name}"? Tasks already created are not affected.`,
      variant: `danger`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    const result = await store.remove(template.id)
    if (result.error) {
      notifyError(`Couldn't delete the task template`)
      logError(`useTaskTemplates: delete failed`, result.error.message)
      return
    }
    notifySuccess(`Task template deleted`)
  }

  return {
    templates,
    saving: store.saving,
    staffOptions,
    staffLoading,
    setSearch,
    editing,
    openNew: () => setEditing(`new`),
    openEdit: setEditing,
    closeEditor: () => setEditing(undefined),
    saveTemplate,
    removeTemplate,
  }
}
